import React, { FC, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Table } from '@unbooking/ui-kit';
import { IStatus, SimpleTrainingRequest } from '@/common/api/digitalOffice';
import { Tip } from '@components';
import { PATHS } from '@/common/path';
import { getAgencies, getEventDate, mapStatusToOption } from '@/components/digital-office/utils';
import { StatusBlock } from '@components';

interface TableUserTrainingRequest {
  id: string;
  organization: string;
  trainingType: string;
  eventDates: string;
  attendees: string;
  status: string;
  statusToOption: Record<string, IStatus>;
}

const requestToTableRequest = (
  request: SimpleTrainingRequest,
  statusToOption: Record<string, IStatus>,
): TableUserTrainingRequest => {
  return {
    id: request.request_id,
    organization: getAgencies(request),
    trainingType: request.program,
    eventDates: getEventDate(request),
    attendees: request.num_of_attendees.toString(),
    status: request.status,
    statusToOption,
  };
};

const TABLE_COLUMNS = [
  {
    label: 'Training number',
    field: 'request_id',
    render: (row: TableUserTrainingRequest) => (
      <Link
        className="text-blue-400 font-medium"
        to={PATHS.userTrainingDetails.replace(':trainingRequestId', row.id)}
      >
        {row.id}
      </Link>
    ),
  },
  {
    label: 'Organization',
    field: 'organization',
    nowrap: true,
    render: (row: TableUserTrainingRequest) => (
      <Tip
        className="inline-block max-w-[150px] overflow-hidden overflow-ellipsis"
        text={row.organization}
      >
        {row.organization}
      </Tip>
    ),
  },
  {
    label: 'Training program',
    field: 'trainingType',
  },
  {
    label: 'Proposed event dates',
    field: 'eventDates',
  },
  {
    label: 'Participants',
    field: 'attendees',
  },
  {
    label: 'Status',
    field: 'status',
    render: (row: TableUserTrainingRequest) => (
      <StatusBlock status={row.status} statusToOption={row.statusToOption} />
    ),
  },
];

interface IUserRequestTable {
  trainingRequests: SimpleTrainingRequest[];
  statuses?: IStatus[];
  page: number;
  setPage: React.Dispatch<React.SetStateAction<number>>;
  rowLimit: number;
  setRowLimit: React.Dispatch<React.SetStateAction<number>>;
  resultsCount: number;
}

export const UserRequestTable: FC<IUserRequestTable> = ({
  trainingRequests,
  statuses = [],
  page,
  setPage,
  rowLimit,
  setRowLimit,
  resultsCount,
}) => {
  const tableTrainingRequests = useMemo(() => {
    const statusesOption = mapStatusToOption(statuses);
    return trainingRequests.map(request => requestToTableRequest(request, statusesOption));
  }, [trainingRequests, statuses]);

  const onOffsetChange = (offset: number) => {
    const page = Math.floor(offset / rowLimit) + 1;
    setPage(page);
  };

  return (
    <Table
      items={tableTrainingRequests}
      columns={TABLE_COLUMNS}
      count={resultsCount}
      limit={rowLimit}
      offset={rowLimit * (page - 1)}
      onChangeLimit={setRowLimit}
      onChangeOffset={onOffsetChange}
      light
    />
  );
};

export default UserRequestTable;
